import {
  boundsOfRects,
  nodeAABB,
  rectFromPoints,
  snapMove,
  snapPointToGrid,
  vDist,
  type BoardNode,
  type ConnectorEndpoint,
  type ConnectorNode,
  type NodeId,
  type Rect,
  type Vec,
} from '@kanvas/board-core';
import { frameChildren, frameParentFor, reparentIntoFrames } from '../../actions/actions';
import type { BoardDoc } from '../../collab/BoardDoc';
import { invalidate, scratch } from '../../state/scratch';
import { useEditor } from '../../state/store';
import { pickInRect, snapCandidates } from '../picking';
import { DRAG_THRESHOLD, SNAP_THRESHOLD_PX, type Gesture, type GestureContext } from '../types';

/**
 * Rubber-band selection.
 *
 * `additive` (shift held at press) unions with the existing selection instead
 * of replacing it; the base selection is captured up front so dragging back and
 * forth does not accumulate.
 */
export function createMarqueeGesture(ctx: GestureContext, additive: boolean): Gesture {
  const origin = ctx.pointer.board;
  const base = additive ? [...useEditor.getState().selection] : [];
  const enteredGroups = new Set(useEditor.getState().enteredGroups);

  return {
    cursor: 'crosshair',
    move(next) {
      const area = rectFromPoints(origin, next.pointer.board);
      scratch.marquee = area;
      scratch.gestureActive = true;

      // Full containment when the primary modifier is held: the same
      // "touch vs enclose" distinction other canvas tools use.
      const mode = next.mods.primary ? 'contain' : 'intersect';
      const hits = pickInRect(next.doc, area, {
        vp: useEditor.getState().viewport,
        enteredGroups,
        mode,
      });
      useEditor.getState().select([...base, ...hits.map((n) => n.id)]);
      invalidate();
    },
    up() {
      scratch.marquee = null;
      scratch.gestureActive = false;
      invalidate();
    },
    cancel() {
      scratch.marquee = null;
      scratch.gestureActive = false;
      useEditor.getState().select(base);
      invalidate();
    },
  };
}

// ---------------------------------------------------------------------------
// Move
// ---------------------------------------------------------------------------

type GeometrySnapshot = Partial<BoardNode>;

function snapshotGeometry(node: BoardNode): GeometrySnapshot {
  if (node.type === 'connector') {
    return {
      start: node.start,
      end: node.end,
      bend: node.bend,
    } as GeometrySnapshot;
  }
  return { x: node.x, y: node.y } as GeometrySnapshot;
}

function shiftEndpoint(ep: ConnectorEndpoint, d: Vec): ConnectorEndpoint {
  // Attached ends follow their node; only free ends translate.
  return ep.kind === 'point' ? { kind: 'point', x: ep.x + d.x, y: ep.y + d.y } : ep;
}

/**
 * Everything that travels with a drag.
 *
 * Group members and frame contents come along, and a connector between two
 * moving nodes is left alone (its endpoints resolve from the nodes, so it
 * follows for free) unless it is selected in its own right.
 */
export function collectMoving(doc: BoardDoc, ids: readonly NodeId[]): BoardNode[] {
  const chosen = new Map<NodeId, BoardNode>();
  const add = (node: BoardNode | undefined) => {
    if (node && !chosen.has(node.id) && !node.locked) chosen.set(node.id, node);
  };

  const queue = [...ids];
  while (queue.length > 0) {
    const id = queue.pop()!;
    const node = doc.get(id);
    if (!node) continue;
    add(node);
    if (node.type === 'group') for (const child of node.children) queue.push(child);
    if (node.type === 'frame') for (const child of frameChildren(doc, node.id)) queue.push(child.id);
  }

  return [...chosen.values()];
}

/**
 * The selection plus every member of every selected group, transitively.
 *
 * Resize and rotate work on this rather than on the raw selection: a group is
 * just a box in the document, so transforming only the group node would leave
 * its members exactly where they were.
 */
export function collectGroupMembers(doc: BoardDoc, ids: readonly NodeId[]): BoardNode[] {
  const chosen = new Map<NodeId, BoardNode>();
  const queue = [...ids];
  while (queue.length > 0) {
    const id = queue.pop()!;
    const node = doc.get(id);
    if (!node || chosen.has(node.id)) continue;
    chosen.set(node.id, node);
    if (node.type === 'group') for (const child of node.children) queue.push(child);
  }
  return [...chosen.values()];
}

export interface MoveGestureOptions {
  /** Skip snapping, used while a modifier disables it. */
  noSnap?: boolean;
}

export function createMoveGesture(
  ctx: GestureContext,
  ids: readonly NodeId[],
  options: MoveGestureOptions = {},
): Gesture {
  const doc = ctx.doc;
  const origin = ctx.pointer.board;
  const moving = collectMoving(doc, ids);

  const originals = new Map<NodeId, GeometrySnapshot>();
  for (const node of moving) originals.set(node.id, snapshotGeometry(node));

  const excluded = new Set(moving.map((n) => n.id));
  const vp0 = useEditor.getState().viewport;
  const candidates = options.noSnap ? [] : snapCandidates(doc, vp0, excluded);
  const startBounds: Rect =
    moving.length > 0
      ? boundsOfRects(moving.map((n) => nodeAABB(n, doc.lookup)))
      : { x: 0, y: 0, w: 0, h: 0 };

  let lastFinals = new Map<NodeId, Partial<BoardNode>>();
  let moved = false;

  const build = (delta: Vec): Map<NodeId, Partial<BoardNode>> => {
    const out = new Map<NodeId, Partial<BoardNode>>();
    for (const node of moving) {
      const original = originals.get(node.id)!;
      if (node.type === 'connector') {
        const conn = node as ConnectorNode;
        out.set(node.id, {
          start: shiftEndpoint((original as Partial<ConnectorNode>).start ?? conn.start, delta),
          end: shiftEndpoint((original as Partial<ConnectorNode>).end ?? conn.end, delta),
          bend: ((original as Partial<ConnectorNode>).bend ?? conn.bend).map((b) => ({
            x: b.x + delta.x,
            y: b.y + delta.y,
          })),
        } as Partial<BoardNode>);
      } else {
        const ox = (original as { x: number }).x;
        const oy = (original as { y: number }).y;
        out.set(node.id, { x: ox + delta.x, y: oy + delta.y } as Partial<BoardNode>);
      }
    }
    return out;
  };

  return {
    cursor: 'move',
    move(next) {
      const vp = useEditor.getState().viewport;
      // A press that wobbles by a pixel is still a click. Without a threshold
      // every selection click wrote an undo step and a peer-visible "move".
      if (!moved && vDist(next.pointer.screen, ctx.pointer.screen) < DRAG_THRESHOLD) return;
      let delta: Vec = {
        x: next.pointer.board.x - origin.x,
        y: next.pointer.board.y - origin.y,
      };

      // Shift locks the drag to the dominant axis.
      if (next.mods.shift) {
        if (Math.abs(delta.x) >= Math.abs(delta.y)) delta = { x: delta.x, y: 0 };
        else delta = { x: 0, y: delta.y };
      }

      const meta = doc.getMeta();
      const snapEnabled = useEditor.getState().snapEnabled && !options.noSnap && !next.mods.primary;

      if (snapEnabled) {
        const probe: Rect = {
          x: startBounds.x + delta.x,
          y: startBounds.y + delta.y,
          w: startBounds.w,
          h: startBounds.h,
        };
        const snap = snapMove(probe, candidates, SNAP_THRESHOLD_PX / vp.zoom, {
          size: meta.gridSize,
          enabled: meta.snapToGrid,
        });
        delta = { x: delta.x + snap.delta.x, y: delta.y + snap.delta.y };
        scratch.guides = snap.guides;
      } else if (meta.snapToGrid) {
        const snapped = snapPointToGrid(
          { x: startBounds.x + delta.x, y: startBounds.y + delta.y },
          meta.gridSize,
        );
        delta = { x: snapped.x - startBounds.x, y: snapped.y - startBounds.y };
        scratch.guides = [];
      } else {
        scratch.guides = [];
      }

      moved = true;
      scratch.gestureActive = true;
      lastFinals = build(delta);
      doc.updateManyEphemeral(lastFinals);

      // Highlight whichever frame this drop would join. Images attach by their
      // centre, so a photo larger than the frame still lights it up.
      const dropTargets = new Set<NodeId>();
      for (const node of moving) {
        const current = doc.get(node.id);
        if (!current) continue;
        const parent = frameParentFor(doc, current);
        if (parent && !excluded.has(parent) && doc.get(parent)?.type === 'frame') {
          dropTargets.add(parent);
        }
      }
      scratch.highlightIds = [...dropTargets];
      invalidate();
    },

    up(next) {
      scratch.guides = [];
      scratch.highlightIds = [];
      scratch.gestureActive = false;
      if (moved && lastFinals.size > 0) {
        doc.commitGesture(originals, lastFinals, () =>
          reparentIntoFrames({ doc, identity: next.identity }, [...lastFinals.keys()]),
        );
      }
      invalidate();
    },

    cancel() {
      scratch.guides = [];
      scratch.highlightIds = [];
      scratch.gestureActive = false;
      if (moved) doc.updateManyEphemeral(originals);
      invalidate();
    },
  };
}
